import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription } from '@/components/ui/alert'
import type { ExtractionPreviewUiState } from './standardExtractionPreviewUiState'

export type SectionExtractionPreviewContentProps = {
  /** `source` shows verbatim extracted fields; `display` supports per-item language toggles. */
  contentVariant?: 'source' | 'display'
  uiState: ExtractionPreviewUiState
  resolveReason: (code: string) => string
  translate: TFunction<'standardsLibrary'>
}

export function useExtractPreviewCopy() {
  const { t: translate } = useTranslation('standardsLibrary')
  const resolveReason = (code: string) =>
    translate(`detail.extractPreview.impedimentReason.${code}`, code)
  return { translate, resolveReason }
}

export function ExtractionPreviewImpedimentBanner({
  label,
  reason,
}: {
  label: string
  reason: string
}) {
  return (
    <Alert variant='warning'>
      <AlertDescription className='text-current'>
        <span className='font-medium'>{label}:</span> {reason}
      </AlertDescription>
    </Alert>
  )
}

export function ExtractionPreviewSectionNotFound({
  reasonCode,
  resolveReason,
  translate,
}: {
  reasonCode: string
  resolveReason: (code: string) => string
  translate: TFunction<'standardsLibrary'>
}) {
  return (
    <div className='space-y-3'>
      <Alert>
        <AlertDescription className='text-current'>
          {resolveReason(reasonCode)}
        </AlertDescription>
      </Alert>
      <p className='text-sm text-muted-foreground'>
        {translate('detail.extractPreview.sectionNotFoundSaveHint')}
      </p>
    </div>
  )
}

export function ExtractionPreviewNoData({
  translate,
}: {
  translate: TFunction<'standardsLibrary'>
}) {
  return (
    <p className='text-sm text-muted-foreground'>
      {translate('detail.extractPreview.noData')}
    </p>
  )
}

export function ExtractionPreviewAllFailed({
  uiState,
  resolveReason,
  translate,
}: {
  uiState: ExtractionPreviewUiState
  resolveReason: (code: string) => string
  translate: TFunction<'standardsLibrary'>
}) {
  return (
    <div className='space-y-4'>
      {uiState.pdfImpediment != null ? (
        <Alert variant='warning'>
          <AlertDescription className='text-current'>
            {resolveReason(uiState.pdfImpediment.reason)}
          </AlertDescription>
        </Alert>
      ) : (
        uiState.sectionImpediment != null && (
          <ExtractionPreviewImpedimentBanner
            label={uiState.sectionLabel}
            reason={resolveReason(uiState.sectionImpediment.reason)}
          />
        )
      )}
      <p className='text-sm text-muted-foreground'>
        {translate('detail.extractPreview.allFailed')}
      </p>
    </div>
  )
}
